// The transaction table's sort-stack model — pure, no React, no data access.
//
// Extracted from TransactionViewer.js so that SortLevelsPanel and the viewer can
// both use it without importing each other, and so the whole interaction is
// unit-testable without rendering a 4,968-row virtualized table.
//
// The interaction this encodes (William, 2026-09-10):
//
//   - a COLUMN-HEADER arrow sorts that column alone and toggles its direction.
//     It is never a way to build a multi-column sort — see nextSortKeys.
//   - MULTI-COLUMN sorting is explicit, in the sort-levels panel: add, remove,
//     reorder, direction per level.
//   - clicking a header arrow while a leveled sort is active drops back to
//     single-column on that column.
//
// It replaced a click-to-layer model that, because the table lands pre-sorted by
// date, meant a user never got a single-column sort and every click silently
// grew a stack they had not asked for.

// What the table lands on. Reset returns here.
export const DEFAULT_SORT_KEYS = [{ field: 'transactionDate', direction: 'asc' }];

// Every field the table can sort on, in column order — also the panel's menu
// order. Keep in step with the sortable column headers in TransactionViewer.
export const SORTABLE_FIELDS = [
  'transactionDate',
  'bankAccountName',
  'cardholderName',
  'description',
  'amount',
  'merchantName',
  'subcategoryName',
  'flagName',
];

export const SORT_FIELD_LABELS = {
  transactionDate: 'Date',
  bankAccountName: 'Account',
  cardholderName: 'Cardholder',
  description: 'Description',
  amount: 'Amount',
  merchantName: 'Merchant',
  subcategoryName: 'Subcategory',
  flagName: 'Flag',
};

// A key with no value to compare. Excel sorts blanks LAST in both directions,
// so blankness is decided here and acted on OUTSIDE the direction flip — see
// the comparator in TransactionViewer.
export const isBlankSortKey = (value) =>
  value === '' || value === null || value === undefined;

// Serializes a sort stack for cheap change detection (memo/ref comparisons).
export const sortKeysSignature = (sortKeys) =>
  (sortKeys || []).map((k) => `${k.field}:${k.direction}`).join('|');

export const isDefaultSortKeys = (sortKeys) =>
  sortKeysSignature(sortKeys) === sortKeysSignature(DEFAULT_SORT_KEYS);

/**
 * What a COLUMN-HEADER arrow click produces: that column, on its own.
 *
 * Returns a one-key stack unconditionally. That is the point — it is how a
 * click from a leveled sort drops back to single-column.
 */
export const nextSortKeys = (sortKeys, field) => {
  const keys = sortKeys || [];

  // Already the sole key -> flip it. Anything else -> replace outright.
  if (keys.length === 1 && keys[0].field === field) {
    return [{ field, direction: keys[0].direction === 'asc' ? 'desc' : 'asc' }];
  }

  return [{ field, direction: 'asc' }];
};

// ────────────────────────────────────────────────
// Sort-levels mutators — what the panel calls.
// Each returns the NEXT stack; none mutates its input.
// ────────────────────────────────────────────────

/**
 * Fields not yet used as a level. A column can appear at most once: a second
 * level on the same field could never change the order, so it would be a
 * control that does nothing.
 */
export const availableSortFields = (sortKeys, visibleFields = SORTABLE_FIELDS) => {
  const used = (sortKeys || []).map((k) => k.field);
  return (visibleFields || SORTABLE_FIELDS).filter((f) => !used.includes(f));
};

export const addSortLevel = (sortKeys, visibleFields = SORTABLE_FIELDS) => {
  const keys = sortKeys || [];
  const free = availableSortFields(keys, visibleFields);
  if (!free.length) return keys;
  return [...keys, { field: free[0], direction: 'asc' }];
};

export const removeSortLevel = (sortKeys, index) =>
  (sortKeys || []).filter((_, i) => i !== index);

export const setSortLevelField = (sortKeys, index, field) =>
  (sortKeys || []).map((k, i) => (i === index ? { field, direction: k.direction } : k));

export const setSortLevelDirection = (sortKeys, index, direction) =>
  (sortKeys || []).map((k, i) => (i === index ? { field: k.field, direction } : k));

/**
 * Promote (delta -1) or demote (delta +1) a level. An out-of-range move is a
 * no-op rather than an error, so the panel can bind the buttons unconditionally
 * and rely on `disabled` for the affordance.
 */
export const moveSortLevel = (sortKeys, index, delta) => {
  const keys = sortKeys || [];
  const target = index + delta;
  if (index < 0 || index >= keys.length || target < 0 || target >= keys.length) return keys;

  const next = keys.slice();
  next[target] = keys[index];
  next[index] = keys[target];
  return next;
};

/**
 * The stack in words — "Subcategory (A-Z), then Merchant (A-Z)".
 *
 * Shown in the totals bar whether the panel is open or closed, and captioned on
 * the printed PDF and the Excel Report Info sheet, so a recipient can account
 * for the row order.
 */
export const describeSortKeys = (sortKeys) => {
  if (!sortKeys?.length) return 'Unsorted';

  return sortKeys
    .map((k) => {
      const label = SORT_FIELD_LABELS[k.field] || k.field;
      const ascending = k.direction === 'asc';
      // Dates and amounts don't read alphabetically.
      let order;
      if (k.field === 'transactionDate') {
        order = ascending ? 'oldest first' : 'newest first';
      } else if (k.field === 'amount') {
        order = ascending ? 'low to high' : 'high to low';
      } else {
        order = ascending ? 'A-Z' : 'Z-A';
      }
      return `${label} (${order})`;
    })
    .join(', then ');
};
